import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';
import PostCard from '../post-card';
import { dropDuplicates } from '../../../../app/functions';
import spinLoaderIcon from '../../../../assets/icons/spin-loader.svg';
import * as popups from '../../../../constants/popup-types';
import { addPopup, forumFindAllPosts } from '../../../../redux/actions';
import './index.scss';

const PostsList = () => {
  const dispatch = useDispatch();

  const { forumConfiguration } = useSelector((store) => store.config.Parameters);
  const translations = useSelector((store) => store.config.Translations);

  const [postsData, setPostsData] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [currentPost, setCurrentPost] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const hasPosts = postsData.length > 0;
  const isInitialLoading = isLoadingPosts && !hasPosts && !hasReachedEnd;

  const handlePostSelect = (postId) => setCurrentPost(currentPost === postId ? 0 : postId);
  const handleLoadingMorePosts = (inView) => inView && !hasReachedEnd && setCurrentPage(currentPage + 1);

  const handleCreatePostClick = () => dispatch(addPopup({
    type: popups.CREATE_FORUM_POST_POPUP,
    afterCreation: () => fetchPostsData(true),
  }));

  const fetchPostsData = (refreshExistingData = false) => {
    setIsLoadingPosts(true);

    if (refreshExistingData) {
      setPostsData([]);
      setHasReachedEnd(false);
      setCurrentPost(0);
      setCurrentPage(0);
    }

    dispatch(forumFindAllPosts(
      // General Configuration
      { page: refreshExistingData ? 0 : currentPage },
      forumConfiguration,

      // Handle Success
      (response) => {
        if (response.data.posts.length > 0) {
          setPostsData(dropDuplicates([
            ...(refreshExistingData ? [] : postsData),
            ...response.data.posts,
          ], '_id'));
        }

        if (response.data.posts.length < response.data.pageSize) {
          setHasReachedEnd(true);
        }

        setIsLoadingPosts(false);
      },

      // Handle Failure
      () => {
        setIsLoadingPosts(false);
        setHasReachedEnd(true);
      },
    ));
  };

  useEffect(fetchPostsData, [currentPage]);

  const ListActions = () => (
    <div className="list-actions">
      <button
        type="button"
        onClick={handleCreatePostClick}
        className="action-button-element"
      >
        {translations['forum_create_post_button']}
      </button>
    </div>
  );

  return (
    <section className="forum-posts-list">
      <ListActions />

      {isInitialLoading ? (
        <>
          <PostCard isSkeleton />
          <PostCard isSkeleton />
          <PostCard isSkeleton />
          <PostCard isSkeleton />
          <PostCard isSkeleton />
        </>
      ) : (
        <>
          {hasPosts && (
            <>
              {postsData.map((postData) => (
                <PostCard
                  key={postData._id}
                  postData={postData}
                  isSelected={currentPost === postData._id}
                  onSelectClick={() => handlePostSelect(postData._id)}
                />
              ))}

              {isLoadingPosts && !hasReachedEnd && (
                <img
                  src={spinLoaderIcon}
                  alt="Loading..."
                  className="loading-icon"
                />
              )}

              {!isLoadingPosts && hasReachedEnd && (
                <div className="info-message">
                  {translations['forum_posts_reached_end']}
                </div>
              )}

              {!isLoadingPosts && !hasReachedEnd && (
                <InView onChange={handleLoadingMorePosts} />
              )}
            </>
          )}

          {!hasPosts && (
            <div className="info-message">
              {translations['forum_posts_not_found']}
            </div>
          )}
        </>
      )}

      {hasPosts && (<ListActions />)}
    </section>
  );
};

export default PostsList;
